import { Amplify, API } from 'aws-amplify';
import { useEffect, useState } from 'react';
import i18n from './i18n';
import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';

import { muiTheme } from './muiTheme';
import { defaultLanding, userTypes } from './utils/enums';
import Application from './components/protected/Application';
import Applications from './components/protected/Applications';
import AnnualStatements from './components/protected/AnnualStatements';
import CatchAll from './components/CatchAll';
import Confirmation from './components/Confirmation';
import Dashboard from './components/protected/Dashboard';
import EnrollmentChange from './components/protected/EnrollmentChange';
import Finances from './components/protected/Finances';
import ForgotPassword from './components/authentication/ForgotPassword';
import Login from './components/authentication/Login';
import ProtectedRoute from './components/ProtectedRoute';
import Reports from './components/protected/Reports';
import Schools from './components/protected/Schools';
import Settings from './components/protected/Settings';
import Signup from './components/authentication/Signup';
import UserSettings from './components/protected/UserSettings';
import UserSettingsOther from './components/protected/userSettingsOther/index';
import Renewals from './components/protected/Renewals';
import Record from './components/protected/Record';
import ApplicationAdmin from './components/protected/adminApplication';
import SchoolInfo from './components/protected/SchoolInfo';
import InvoiceDetailsPage from './components/protected/invoiceDetails';
import MoveRecordPage from './components/protected/moveRecordPage';
import NotFound from './components/notFound';
import SchoolDashboard from './components/protected/schoolDashboard';
import InvoicingHistory from './components/protected/invoicingHistory';
import DownloadDashboard from './components/protected/downloadDashboard';
import { ShipyardContext } from './components/Context';

import './App.scss';

window.Shipyard = {};
Object.defineProperty(window.Shipyard, 'Version', {
    get: () => {
        let promises = [];
        promises.push(API.get('ShipyardAPINoAuth', '/version'));
        promises.push(fetch('./build.json').then((resp) => resp.json()));
        Promise.all(promises).then(([apiVersion, uiVersion]) => {
            console.log({
                API: apiVersion.version,
                UI: uiVersion.version,
            });
        });
        return 'Gathering version information...';
    },
});
Amplify.configure({
    Auth: {
        identityPoolId: '',
        region: process.env.REACT_APP_COGNITO_REGION,
        userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
        userPoolWebClientId: process.env.REACT_APP_COGNITO_USER_POOL_WEB_CLIENT_ID,
    },
    API: {
        endpoints: [
            {
                name: 'ShipyardAPI',
                endpoint: process.env.REACT_APP_API_ENDPOINT,
                custom_header: async () => {
                    return { Authorization: `Bearer ${(await Amplify.Auth.currentSession()).getIdToken().getJwtToken()}` };
                },
            },
            {
                name: 'ShipyardAPINoAuth',
                endpoint: process.env.REACT_APP_API_ENDPOINT,
            },
        ],
    },
});

function App() {
    const defaultUserState = {
        ClientOrgId: -1,
        ClientHeaderImage: '',
        ClientHeaderText: '',
        ClientName: '',
        FirstName: '',
        LastName: '',
        LanguagePreference: i18n.language,
        PhoneNumber: '',
        UserName: '',
        UserType: -1,
        Retrieved: false,
    };
    const [user, setUser] = useState(null);
    const [userSettings, setUserSettings] = useState(defaultUserState);
    const [userType, setUserType] = useState(userSettings.UserType);
    const [listSettings, setListSettings] = useState({
        target: null,
        reducerState: null,
        useCache: false,
    });
    const [moveConfig, setMoveConfig] = useState(moveOptions);

    const setAppInstId = (id) => setMoveConfig({ ...moveConfig, applicationInstanceId: id });

    useEffect(() => {
        setUserType(userSettings.UserType);
    }, [userSettings.UserType]);

    return (
        <ShipyardContext.Provider
            value={{
                user,
                userSettings,
                moveConfig,
                setMoveConfig,
                resetMoveConfig: () => setMoveConfig(moveOptions),
                setAppInstId,
                updateUser: setUser,
                updateUserSettings: setUserSettings,
                resetValue: defaultUserState,
                listSettings,
                setListSettings,
            }}
        >
            <ThemeProvider theme={muiTheme}>
                <Router>
                    <Switch>
                        <Route path="/confirmation">
                            <Confirmation />
                        </Route>
                        <Route path="/forgotPassword">
                            <ForgotPassword />
                        </Route>
                        <Route path="/signup">
                            <Signup />
                        </Route>
                        <Route path="/signin">
                            <Login />
                        </Route>
                        <Route
                            exact
                            path="/"
                        >
                            <Redirect to={defaultLanding(userType)} />
                        </Route>
                        <ProtectedRoute
                            path="/sitesettings/user/:userId(\d+)"
                            restrictedTo={[userTypes.Admin, userTypes.ScholarshipOrg]}
                            component={UserSettingsOther}
                        />
                        <ProtectedRoute
                            path="/sitesettings"
                            component={Settings}
                            restrictedTo={[userTypes.Admin]}
                        />
                        <ProtectedRoute
                            path="/dashboard"
                            component={Dashboard}
                            restrictedTo={[userTypes.ScholarshipOrg]}
                        />
                        <ProtectedRoute
                            exact
                            path="/application/:applicationId(\d+)"
                            component={Application}
                            restrictedTo={[userTypes.Parent, userTypes.PrivateSchool, userTypes.ScholarshipOrg]}
                        />
                        <ProtectedRoute
                            path="/applications"
                            component={Applications}
                            restrictedTo={[userTypes.Parent, userTypes.PrivateSchool, userTypes.ScholarshipOrg]}
                        />
                        <ProtectedRoute
                            path="/schools"
                            component={Schools}
                            restrictedTo={[userTypes.PrivateSchool, userTypes.ScholarshipOrg]}
                        />
                        <ProtectedRoute
                            path="/reports"
                            component={Reports}
                            restrictedTo={[userTypes.ScholarshipOrg, userTypes.Admin]}
                        />
                        <ProtectedRoute
                            path="/settings"
                            component={Settings}
                            restrictedTo={[userTypes.PrivateSchool, userTypes.ScholarshipOrg]}
                        />
                        <ProtectedRoute
                            path="/usersettings"
                            component={UserSettings}
                            restrictedTo={[userTypes.Parent, userTypes.PrivateSchool, userTypes.Admin]}
                        />
                        <ProtectedRoute
                            exact
                            path="/students"
                            component={SchoolDashboard}
                            restrictedTo={[userTypes.PrivateSchool, userTypes.ScholarshipOrg]}
                        />
                        <ProtectedRoute
                            exact
                            path="/students/renewals"
                            component={Renewals}
                            restrictedTo={[userTypes.PrivateSchool, userTypes.ScholarshipOrg]}
                        />
                        <ProtectedRoute
                            exact
                            path="/students/application/:applicationId(\d+)"
                            component={Record}
                            restrictedTo={[userTypes.PrivateSchool]}
                        />
                        <ProtectedRoute
                            exact
                            path="/scholarship/application/:applicationId(\d+)"
                            component={Record}
                            restrictedTo={[userTypes.ScholarshipOrg]}
                        />
                        <ProtectedRoute
                            exact
                            path="/admin/application/:applicationId(\d+)"
                            component={ApplicationAdmin}
                            restrictedTo={[userTypes.ScholarshipOrg]}
                        />
                        <ProtectedRoute
                            exact
                            path="/actions/move-record"
                            component={MoveRecordPage}
                            restrictedTo={[userTypes.ScholarshipOrg]}
                        />
                        <ProtectedRoute
                            exact
                            path="/schoolinfo"
                            component={SchoolInfo}
                            restrictedTo={[userTypes.PrivateSchool]}
                        />
                        <ProtectedRoute
                            exact
                            path="/finances"
                            component={Finances}
                            restrictedTo={[userTypes.PrivateSchool, userTypes.ScholarshipOrg, userTypes.Admin]}
                        />
                        <ProtectedRoute
                            exact
                            path="/finances/invoices/:invoiceId(\d+)"
                            component={InvoiceDetailsPage}
                            restrictedTo={[userTypes.PrivateSchool, userTypes.ScholarshipOrg, userTypes.Admin]}
                        />
                        <ProtectedRoute
                            exact
                            path="/finances/annualStatement/:applicationId(\d+)"
                            component={AnnualStatements}
                            restrictedTo={[userTypes.PrivateSchool, userTypes.ScholarshipOrg, userTypes.Admin]}
                        />
                        <ProtectedRoute
                            exact
                            path="/scholarship/application/:applicationId(\d+)/enrollment_change"
                            component={EnrollmentChange}
                            restrictedTo={[userTypes.ScholarshipOrg, userTypes.Admin]}
                        />
                        <ProtectedRoute
                            exact
                            path="/invoice-history/:uniqueId(\d+)/:termId(\d+)"
                            component={InvoicingHistory}
                            restrictedTo={[userTypes.ScholarshipOrg, userTypes.Admin]}
                        />
                        <ProtectedRoute
                            exact
                            path="/downloads"
                            component={DownloadDashboard}
                            restrictedTo={[userTypes.Parent, userTypes.PrivateSchool, userTypes.ScholarshipOrg, userTypes.Admin]}
                        />
                        <Route
                            path="/not-found"
                            component={NotFound}
                        />
                        <Route component={CatchAll} />
                    </Switch>
                </Router>
            </ThemeProvider>
        </ShipyardContext.Provider>
    );
}

export default App;

const moveOptions = {
    dataKey: '',
    label1: '',
    label2: '',
    description1: '',
    description2: '',
    moveViewTitle: '',
    inputPlaceholder: '',
    possibleMatchesLanguage: '',
    sort: '',
    getURL: (id) => '',
    updateURL: (id) => '',
    buildBody: (target, toRecord) => ({}),
    buildComparison: (target, toRecord, modalContentList) => {},
    buildConfirmDescription: (target, toRecord, modalContentOverride) => {},
};
